<script setup lang="ts">
import AppContextPanel from '@/components/app-shell/AppContextPanel.vue'
import FinestAssistantPanel from '@/components/FinestAssistantPanel.vue'
import FinestButton from '@/components/FinestButton.vue'
import PropertyDataSection from '@/components/portfolio/PropertyDataSection.vue'
import { portfolioTabs, type PortfolioTab } from '@/pages/investors/portfolio/tabs'
import type { ExtendedEnum, PersonGroup } from '@/types'
import type { FieldStateMap } from '@/utils/field-state'
import EstateObjectsController from '@controllers/Investors/EstateObjectsController'
import ProjectsController from '@controllers/Investors/ProjectsController'
import { Head, router } from '@inertiajs/vue3'
import { useConfirm } from 'primevue/useconfirm'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import AttachmentData = App.Data.AttachmentData
import DocumentSectionData = App.Data.Documents.DocumentSectionData
import DocumentTypeOptionData = App.Data.Documents.DocumentTypeOptionData
import EstateObjectData = App.Data.EstateObject.EstateObjectData
import LoanData = App.Data.EstateObject.LoanData
import ModernizationData = App.Data.EstateObject.ModernizationData
import RentalUnitData = App.Data.EstateObject.RentalUnitData
import ElevatorType = App.Enums.EstateObject.ElevatorType
import EnergyCertificateType = App.Enums.EstateObject.EnergyCertificateType
import EnergySourceType = App.Enums.EstateObject.EnergySourceType
import EstateObjectType = App.Enums.EstateObject.EstateObjectType
import HeatingType = App.Enums.EstateObject.HeatingType
import RoofType = App.Enums.EstateObject.RoofType
import WindowType = App.Enums.EstateObject.WindowType
import CollateralType = App.Enums.EstateObject.Loans.CollateralType
import FinancingType = App.Enums.EstateObject.Loans.FinancingType
import MezzanineType = App.Enums.EstateObject.Loans.MezzanineType

interface ActiveFinancingRequest {
  id: string
}

const props = defineProps<{
  estateObject: EstateObjectData
  completion: number
  fieldStates: FieldStateMap
  photos: AttachmentData[]
  documents: DocumentSectionData
  documentTypeOptions: DocumentTypeOptionData[]
  loans: LoanData[]
  rentalUnits: RentalUnitData[]
  modernizations: ModernizationData[]
  estateObjectTypes: ExtendedEnum<EstateObjectType>[]
  heating_types: ExtendedEnum<HeatingType>[]
  energy_source_types: ExtendedEnum<EnergySourceType>[]
  energy_certificate_types: ExtendedEnum<EnergyCertificateType>[]
  elevator_types: ExtendedEnum<ElevatorType>[]
  roof_types: ExtendedEnum<RoofType>[]
  window_types: ExtendedEnum<WindowType>[]
  financing_types: ExtendedEnum<FinancingType>[]
  mezzanine_types: ExtendedEnum<MezzanineType>[]
  collateral_types: ExtendedEnum<CollateralType>[]
  improvement_types: string[]
  landRegisterChargeDefaults: Record<string, { label: string; rate: number }>
  personOptions: PersonGroup[]
  activeFinancingRequests: ActiveFinancingRequest[]
  returnToProjectId?: string | null
  initialTab?: string | null
}>()

const { t } = useI18n()

const activeTab = ref<PortfolioTab>(portfolioTabs.find((tab) => tab === props.initialTab) ?? 'overview')

const objectType = computed(() =>
  props.estateObject.estate_object_type ? t(`enums.estateObjectType.${props.estateObject.estate_object_type}`) : null,
)

const eyebrow = computed(() => [t('eyebrow'), objectType.value].filter(Boolean).join(' · '))

const title = computed(() => {
  const street = [props.estateObject.street, props.estateObject.house_number].filter(Boolean).join(' ')
  const city = [props.estateObject.zip_code, props.estateObject.city].filter(Boolean).join(' ')

  return [street, city].filter(Boolean).join(', ') || t('eyebrow')
})

const hasActiveFinancingRequest = computed(() => props.activeFinancingRequests.length > 0)

const confirm = useConfirm()

const confirmDelete = () => {
  const id = props.estateObject.id

  if (!id || hasActiveFinancingRequest.value) {
    return
  }

  confirm.require({
    header: t('delete.header'),
    message: t('delete.message', { title: title.value }),
    rejectProps: { label: t('delete.reject'), severity: 'secondary', outlined: true },
    acceptProps: { label: t('delete.accept'), severity: 'danger' },
    accept: () => router.delete(EstateObjectsController.destroy(id).url),
  })
}
</script>

<template>
  <Head :title="title" />

  <AppContextPanel>
    <FinestAssistantPanel :completion="completion" context="property" />
  </AppContextPanel>

  <div class="mb-4">
    <Link :href="returnToProjectId ? ProjectsController.show(returnToProjectId) : EstateObjectsController.index()">
      <FinestButton
        :label="returnToProjectId ? t('backToRequest') : t('back')"
        icon="pi pi-arrow-left"
        variant="ghost"
        size="sm" />
    </Link>
  </div>

  <div class="mb-6 flex flex-wrap items-start justify-between gap-4">
    <div class="min-w-0">
      <p class="section-label">{{ eyebrow }}</p>
      <h1 class="page-title">{{ title }}</h1>
    </div>

    <FinestButton
      variant="ghost"
      size="sm"
      icon="pi pi-trash"
      :label="t('delete.action')"
      :disabled="hasActiveFinancingRequest"
      :title="hasActiveFinancingRequest ? t('activeFinancingRequest.deleteBlocked') : undefined"
      class="flex-none"
      @click="confirmDelete" />
  </div>

  <div v-if="hasActiveFinancingRequest" class="mb-6">
    <Message severity="info" icon="pi pi-file-edit">
      {{ t('activeFinancingRequest.notice', activeFinancingRequests.length) }}
    </Message>
  </div>

  <PropertyDataSection
    v-model:tab="activeTab"
    :estate-object="estateObject"
    :field-states="fieldStates"
    :photos="photos"
    :documents="documents"
    :document-type-options="documentTypeOptions"
    :loans="loans"
    :rental-units="rentalUnits"
    :modernizations="modernizations"
    :estate-object-types="estateObjectTypes"
    :heating-types="heating_types"
    :energy-source-types="energy_source_types"
    :energy-certificate-types="energy_certificate_types"
    :elevator-types="elevator_types"
    :roof-types="roof_types"
    :window-types="window_types"
    :financing-types="financing_types"
    :mezzanine-types="mezzanine_types"
    :collateral-types="collateral_types"
    :improvement-types="improvement_types"
    :land-register-charge-defaults="landRegisterChargeDefaults"
    :person-options="personOptions"
    :aria-label="title" />
</template>

<i18n>
{
  "de": {
    "eyebrow": "Portfolio",
    "back": "Zum Portfolio",
    "backToRequest": "Zur Finanzierungsanfrage",
    "delete": {
      "action": "Löschen",
      "header": "Immobilie löschen",
      "message": "„{title}“ wird aus deinem Portfolio entfernt. Die Daten bleiben archiviert und können vom Support wiederhergestellt werden.",
      "reject": "Abbrechen",
      "accept": "Löschen"
    },
    "activeFinancingRequest": {
      "notice": "Auf diese Immobilie läuft eine Finanzierungsanfrage. | Auf diese Immobilie läuft eine Finanzierungsanfrage. | Auf diese Immobilie laufen {count} Finanzierungsanfragen.",
      "deleteBlocked": "Solange eine Finanzierungsanfrage auf dieser Immobilie läuft, kann sie nicht gelöscht werden."
    }
  },
  "en": {
    "eyebrow": "Portfolio",
    "back": "Back to portfolio",
    "backToRequest": "Back to financing request",
    "delete": {
      "action": "Delete",
      "header": "Delete property",
      "message": "“{title}” will be removed from your portfolio. The data stays archived and can be restored by support.",
      "reject": "Cancel",
      "accept": "Delete"
    },
    "activeFinancingRequest": {
      "notice": "A financing request is running on this property. | A financing request is running on this property. | {count} financing requests are running on this property.",
      "deleteBlocked": "The property cannot be deleted while a financing request is running on it."
    }
  }
}
</i18n>
